import { useState, useEffect, useRef } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { useShopify } from "@app/hooks/useShopify"
import { useShopifyProductVariants } from "@app/hooks/useShopify/useShopifyProductVariants"
import { useConfigContext } from "@app/providers/config"
import { isRecommendableProduct } from "@app/utils/recommendations"
import { isTrackingAllowed } from "@app/utils/tracking"

const BEST_SELLERS_SORT_NAME = "Best Sellers"
const MAX_TRENDING = 20
const DEFAULT_TRENDING_TITLE = "Community favourites"
// Facet used to scope trending to a single collection. Must be declared as an
// attributeForFaceting in the Algolia index (it already is — see facet usage in providers/algolia.tsx).
const COLLECTION_FACET_NAME = "collections"

type UseAlgoliaTrendingOptions = {
  // When provided, trending is scoped to this collection handle and falls back to global trending if empty.
  collectionHandle?: string
}

const useAlgoliaTrending = ({ collectionHandle }: UseAlgoliaTrendingOptions = {}) => {
  const [products, setProducts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const { adminProductNormaliserAlgolia } = useShopify()
  const { fetchProductVariants } = useShopifyProductVariants()
  const {
    settings: { routes },
  } = useConfigContext()

  const normalizerRef = useRef(adminProductNormaliserAlgolia)
  normalizerRef.current = adminProductNormaliserAlgolia

  const fetchVariantsRef = useRef(fetchProductVariants)
  fetchVariantsRef.current = fetchProductVariants

  const routesRef = useRef(routes)
  routesRef.current = routes

  const { config } = useStaticQuery<GatsbyTypes.StaticAlgoliaTrendingQuery>(graphql`
    query StaticAlgoliaTrending {
      config: sanitySettingAlgolia {
        applicationId
        searchOnlyApiKey
        trendingTitle
        sortOptions {
          name
          handle
        }
      }
    }
  `)

  const title = config?.trendingTitle || DEFAULT_TRENDING_TITLE

  useEffect(() => {
    if (!config?.applicationId || !config?.searchOnlyApiKey) return

    const indexHandle = config.sortOptions?.find((opt: any) => opt.name === BEST_SELLERS_SORT_NAME)?.handle
    if (!indexHandle) return

    let cancelled = false
    setLoading(true)

    const userToken = localStorage.getItem("algolia_user_token")

    const baseRequest = {
      indexName: indexHandle,
      model: "trending-items" as const,
      threshold: 0,
      maxRecommendations: MAX_TRENDING,
      queryParameters: {
        filters: "NOT tags:review-product",
        // Adds a queryID to the response so Insights events can be attributed to this request
        clickAnalytics: isTrackingAllowed(),
        ...(userToken ? { userToken } : {}),
      },
    }

    const fetchTrendingHits = async (request: Record<string, unknown>): Promise<{ hits: any[]; queryID?: string }> => {
      const response = await fetch(`https://${config.applicationId}.algolia.net/1/indexes/*/recommendations`, {
        method: "POST",
        headers: {
          "X-Algolia-Application-Id": config.applicationId!,
          "X-Algolia-API-Key": config.searchOnlyApiKey!,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ requests: [request] }),
      })

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null)
        console.error("Algolia trending error body:", errorBody)
        throw new Error(`Algolia trending error: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      return { hits: data.results?.[0]?.hits ?? [], queryID: data.results?.[0]?.queryID }
    }

    const run = async () => {
      try {
        let hits: any[] = []
        let queryID: string | undefined

        // Try collection-scoped trending first when a handle is provided
        if (collectionHandle) {
          try {
            ;({ hits, queryID } = await fetchTrendingHits({
              ...baseRequest,
              facetName: COLLECTION_FACET_NAME,
              facetValue: collectionHandle,
            }))
          } catch (scopedError) {
            console.warn("[Trending] Scoped trending failed, falling back to global:", scopedError)
          }
        }

        // Fallback to global trending if no collection scope, or if the scoped call returned nothing
        if (!hits.length) {
          ;({ hits, queryID } = await fetchTrendingHits(baseRequest))
        }

        const handles = [...new Set(hits.filter((h: any) => h?.handle).map((h: any) => h.handle))] as string[]

        const variantResults = await Promise.all(
          handles.map(async handle => {
            try {
              const variants = await fetchVariantsRef.current(handle)
              return { handle, variants }
            } catch {
              return { handle, variants: [] }
            }
          })
        )

        const variantsMap = new Map(variantResults.map(({ handle, variants }) => [handle, variants]))

        const normalizedProducts = hits
          .map((hit: any, position: number) => {
            const variants = hit?.handle ? variantsMap.get(hit.handle) : undefined
            const normalized = normalizerRef.current(hit, routesRef.current, variants)
            if (!normalized) return normalized
            // Stamp Insights attribution fields so clicks/conversions link back to this request
            return {
              ...normalized,
              queryID,
              objectID: hit.objectID,
              indexName: indexHandle,
              position: position + 1,
            }
          })
          .filter(Boolean)
          .filter(isRecommendableProduct)
          .map((p: any) => ({
            ...p,
            option_names:
              p?.option_names ??
              p?.options?.map((o: any) => o?.name).filter(Boolean) ??
              p?.variants?.[0]?.selectedOptions?.map((o: any) => o?.name).filter(Boolean) ??
              [],
          }))

        if (!cancelled) setProducts(normalizedProducts)
      } catch (error) {
        console.error("Error fetching trending products:", error)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [config?.applicationId, config?.searchOnlyApiKey, collectionHandle]) // eslint-disable-line react-hooks/exhaustive-deps

  return { products, loading, title }
}

export { useAlgoliaTrending }
